import {FC, useEffect, useMemo, useState} from "react";
import {App, ConfigProvider, Empty, Spin, Table, Tag, Typography, theme} from "antd";
import {TrophyOutlined} from "@ant-design/icons";
import AxiosServices from "../Services/axiosServices";
import {contrastColor} from "../Services/color";
import {useGlobal} from "../config/GlobalContext";

const ACCENT = '#203923';
const {Title, Text, Paragraph} = Typography;

type Row = {
    cmpedt_idt: number;
    groupes: string;
    match_label: string;
    winner_grp: string;
    winner_team_idt: number | null;
    winner_team: string | null;
    third_grp: string;
    third_team_idt: number;
    third_team: string;
};

const TeamCell: FC<{teamIdt: number | null | undefined; name: string | null | undefined; placeholder: string}> = ({teamIdt, name, placeholder}) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 8, minWidth: 0}}>
        {teamIdt != null && (
            <img
                src={`/images/${teamIdt}.png`}
                alt={name ?? ''}
                style={{width: 22, height: 22, objectFit: 'contain', flexShrink: 0}}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
            />
        )}
        <span
            style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0}}
            title={name ?? ''}
        >
            {name ?? <Text italic style={{opacity: 0.6}}>{placeholder}</Text>}
        </span>
    </div>
);

const ThirdOpponents: FC = () => {
    const {currentCmpEdt, groups} = useGlobal();
    const {message} = App.useApp();
    const [rows, setRows] = useState<Row[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!currentCmpEdt) { setRows([]); return; }
        let cancelled = false;
        setLoading(true);
        AxiosServices.getThirdOpponents(currentCmpEdt)
            .then(data => { if (!cancelled) setRows(data ?? []); })
            .catch(err => {
                console.error('getThirdOpponents failed', err);
                if (!cancelled) message.error("Échec chargement des adversaires 3èmes.");
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [currentCmpEdt, message]);

    const grpColor = useMemo(() => {
        const m = new Map<string, string | undefined>();
        for (const g of groups) {
            if (g.Grp_Cod) m.set(g.Grp_Cod.toUpperCase(), g.clr);
        }
        return m;
    }, [groups]);

    const qualifierKey = rows[0]?.groupes ?? '';

    const columns = useMemo(() => [
        {
            title: 'Match',
            dataIndex: 'match_label',
            width: 80,
            align: 'center' as const,
            render: (label: string) => <Text strong>{label}</Text>,
        },
        {
            title: 'Vainqueur de groupe',
            dataIndex: 'winner_team',
            render: (_: any, row: Row) => (
                <div style={{display: 'flex', alignItems: 'center', gap: 8, minWidth: 0}}>
                    <Tag style={{
                        margin: 0, minWidth: 26, textAlign: 'center', fontWeight: 700,
                        background: grpColor.get(row.winner_grp) || undefined,
                        borderColor: grpColor.get(row.winner_grp) || undefined,
                        color: contrastColor(grpColor.get(row.winner_grp)),
                    }}>{row.winner_grp}</Tag>
                    <TeamCell teamIdt={row.winner_team_idt} name={row.winner_team} placeholder="(à déterminer)"/>
                </div>
            ),
        },
        {
            title: '',
            width: 40,
            align: 'center' as const,
            render: () => <Text type="secondary">vs</Text>,
        },
        {
            title: '3ème qualifié',
            dataIndex: 'third_team',
            render: (_: any, row: Row) => (
                <div style={{display: 'flex', alignItems: 'center', gap: 8, minWidth: 0}}>
                    <Tag style={{
                        margin: 0, minWidth: 36, textAlign: 'center', fontWeight: 700,
                        background: grpColor.get(row.third_grp) || undefined,
                        borderColor: grpColor.get(row.third_grp) || undefined,
                        color: contrastColor(grpColor.get(row.third_grp)),
                    }}>3{row.third_grp}</Tag>
                    <TeamCell teamIdt={row.third_team_idt} name={row.third_team} placeholder="(à déterminer)"/>
                </div>
            ),
        },
    ], [grpColor]);

    if (!currentCmpEdt) {
        return (
            <div style={{padding: 24}}>
                <Empty description="Sélectionne d'abord une compétition dans la sidebar."/>
            </div>
        );
    }

    return (
        <div style={{padding: 24, background: ACCENT, minHeight: '100%'}}>
            <div style={{maxWidth: 1024, margin: '0 auto'}}>
                <Title level={2} style={{color: '#fff', textAlign: 'center'}}>
                    <TrophyOutlined/> Adversaires 3èmes des vainqueurs de groupe
                </Title>
                <Paragraph style={{textAlign: 'center', color: '#cfd8d3', marginBottom: 16}}>
                    Affectations FIFA des meilleurs 3èmes pour les 8èmes de finale.
                    {qualifierKey && (
                        <> Combinaison qualifiée actuelle&nbsp;: <Text code style={{color: '#fff'}}>{qualifierKey}</Text>.</>
                    )}
                </Paragraph>

                {loading ? (
                    <div style={{textAlign: 'center', padding: 48}}><Spin/></div>
                ) : rows.length === 0 ? (
                    <Empty description={<span style={{color: '#cfd8d3'}}>
                        Pas encore de combinaison résolvable (template manquant ou phase de groupes incomplète).
                    </span>}/>
                ) : (
                    <ConfigProvider theme={{algorithm: theme.darkAlgorithm}}>
                        <div style={{
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 10,
                            padding: 12,
                        }}>
                            <Table
                                size="middle"
                                pagination={false}
                                showHeader
                                dataSource={rows}
                                rowKey="match_label"
                                columns={columns as any}
                            />
                        </div>
                    </ConfigProvider>
                )}
            </div>
        </div>
    );
};

export default ThirdOpponents;
